const API_BASE = '/api';
const SESSION_KEY = 'agentomy_api_key';

// Auth helpers
export function getApiKey(): string | null {
  try { return sessionStorage.getItem(SESSION_KEY); } catch { return null; }
}

export function setApiKey(key: string): void {
  try { sessionStorage.setItem(SESSION_KEY, key); } catch { /* noop */ }
}

export function isAuthenticated(): boolean {
  return !!getApiKey();
}

export function logout(): void {
  try { sessionStorage.removeItem(SESSION_KEY); } catch { /* noop */ }
  // Redirect to root -- app.tsx auth gate will show LoginPage
  window.location.href = '/';
}

// Build headers with optional auth key
function authHeaders(extra?: Record<string, string>): Record<string, string> {
  const key = getApiKey();
  const headers: Record<string, string> = { ...extra };
  if (key) headers['X-API-Key'] = key;
  return headers;
}

// Handle 401 uniformly: clear session and reload
function handle401(): null {
  logout();
  return null;
}

async function safeFetch(url: string): Promise<any> {
  try {
    const res = await fetch(url, { headers: authHeaders() });
    if (res.status === 401) return handle401();
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

// Health & system
export const fetchHealth = () => safeFetch('/health');
export const fetchMetrics = () => safeFetch(`${API_BASE}/metrics/enhanced`);

// Trust-anomaly detector (CODE-002 mounted 2026-06-19; bundled .ts/.js per
// feedback_asi09_financial_strengthen_2026_05_27 dual-maintain). Background
// scan loop runs every 5 minutes inside the capsule; manual scan also supported.
export const fetchTrustAnomalyStats = () => safeFetch(`${API_BASE}/trust-anomaly-detector/statistics`);
export const fetchTrustAnomalyNetwork = () => safeFetch(`${API_BASE}/trust-anomaly-detector/network-metrics`);
export const fetchTrustAnomalyHealth = () => safeFetch(`${API_BASE}/trust-anomaly-detector/health`);

// Cross-fleet threat-intelligence graph (Phase 1, intra-tenant). The API scopes the response
// BY IDENTITY: a deployment operator sees the fleet aggregate + moat metric; any other caller
// sees only their own tenant's protection posture. The view renders whichever shape it gets
// (keyed on data.scope) -- the audience boundary lives at the API, not in the UI.
export const fetchThreatGraphStats = () => safeFetch(`${API_BASE}/threat-graph/stats`);
export async function triggerTrustAnomalyScan(): Promise<any> {
  try {
    const res = await fetch(`${API_BASE}/trust-anomaly-detector/scan`, {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({}),
    });
    if (res.status === 401) return handle401();
    if (!res.ok) return null;
    return res.json();
  } catch { return null; }
}

// IPI defense -- agentjacking + indirect prompt injection (Phase 1.1/1.5).
// /api/ingest/classify returns source-trust classification only.
// /api/ingest/adjudicate runs full content + source verdict (used post-action).
export async function classifyIngestSource(source: { uri?: string; channel?: string; trust?: string }): Promise<any> {
  try {
    const res = await fetch(`${API_BASE}/ingest/classify`, {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ source }),
    });
    if (res.status === 401) return handle401();
    if (!res.ok) return null;
    return res.json();
  } catch { return null; }
}
export async function adjudicateIngest(content: string, source: { uri?: string; channel?: string; trust?: string }): Promise<any> {
  try {
    const res = await fetch(`${API_BASE}/ingest/adjudicate`, {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ content, source }),
    });
    if (res.status === 401) return handle401();
    if (!res.ok) return null;
    return res.json();
  } catch { return null; }
}

// Agents & Core System
export const fetchAgents = () => safeFetch(`${API_BASE}/agents`);
export const fetchBlackboxStatus = () => safeFetch(`${API_BASE}/blackbox/status`);
export const fetchBlackboxAudit = () => safeFetch(`${API_BASE}/blackbox/audit`);
export const fetchBlackboxConfig = () => safeFetch(`${API_BASE}/blackbox/config/export`);
export const fetchActiveRelays = () => safeFetch(`${API_BASE}/blackbox/relay/active`);
export const fetchSentienceEvents = () => safeFetch(`${API_BASE}/blackbox/sentience/events`);
export const fetchDecisionHistory = () => safeFetch(`${API_BASE}/blackbox/decision/history`);
export const fetchPersonaHistory = () => safeFetch(`${API_BASE}/blackbox/persona/history`);
export const fetchTraceLocks = () => safeFetch(`${API_BASE}/blackbox/traces/locks`);

// Agent Topology
export const fetchTopology = () => safeFetch(`${API_BASE}/agent/topology`);

// Pipeline
export const fetchPipelineStages = () => safeFetch(`${API_BASE}/pipeline/stages`);

// Personas
export const fetchPersonas = () => safeFetch(`${API_BASE}/personas`);

// AI
export const fetchAIHealth = () => safeFetch(`${API_BASE}/ai/health`);
export const fetchAIModels = () => safeFetch(`${API_BASE}/ai/models`);

// Modules (from src routes)
export const fetchCapsuleList = () => safeFetch(`${API_BASE}/capsules`);

// Chat
export async function sendChat(
  messages: Array<{role: string, content: string}>,
  options: { conversationId?: string, capability?: string } = {}
) {
  const extra: Record<string, string> = { 'Content-Type': 'application/json' };
  if (options.conversationId) extra['X-Conversation-ID'] = options.conversationId;

  try {
    const res = await fetch(`${API_BASE}/ai/chat`, {
      method: 'POST',
      headers: authHeaders(extra),
      body: JSON.stringify({ messages, capability: options.capability })
    });
    if (res.status === 401) { handle401(); return null; }
    if (!res.ok) throw new Error(`Chat API error: ${res.status}`);
    return await res.json();
  } catch {
    return null;
  }
}

// Audit trail
export const fetchAuditExport = (limit = 100) => safeFetch(`${API_BASE}/audit/export?limit=${limit}`);
export const fetchAuditIntegrity = () => safeFetch(`${API_BASE}/audit/export/integrity`);
export const fetchAuditStatus = () => safeFetch(`${API_BASE}/audit/export/status`);

// Emergency Halt (Kill Switch)
export async function triggerEmergencyHalt() {
  try {
    const res = await fetch(`${API_BASE}/agents/sovereign-override`, {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ authToken: 'demo-override', operatorId: 'dashboard-user' })
    });
    if (res.status === 401) { handle401(); return { error: 'Unauthorized' }; }
    return await res.json();
  } catch {
    return { error: 'Failed to trigger emergency halt' };
  }
}

// Helper to get all dashboard data in one call
export async function fetchAllDashboardData() {
  const [health, metrics, agents, personas, audit, relays, sentience] = await Promise.all([
    fetchHealth(),
    fetchMetrics(),
    fetchAgents(),
    fetchPersonas(),
    fetchBlackboxAudit(),
    fetchActiveRelays(),
    fetchSentienceEvents(),
  ]);
  return { health, metrics, agents, personas, audit, relays, sentience };
}

// Command Center - Fleet
export const fetchClawHealth = () => safeFetch(`${API_BASE}/claw/health`);
export const fetchGovernanceSummary = () => safeFetch(`${API_BASE}/governance/summary`);

// Command Center - Audit
export const fetchAuditPage = (page: number, limit = 20) =>
  safeFetch(`${API_BASE}/audit/export?page=${page}&limit=${limit}`);
export const fetchAuditAnchors = () => safeFetch(`${API_BASE}/audit/anchors`);

// Command Center - Steer
export async function postClawAuthorize(data: { agentId: string; action: string; tier: string }) {
  try {
    const res = await fetch(`${API_BASE}/claw/authorize`, {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(data),
    });
    if (res.status === 401) { handle401(); return { error: 'Unauthorized' }; }
    if (!res.ok) throw new Error(`${res.status}`);
    return await res.json();
  } catch (e: any) {
    return { error: e.message };
  }
}

export async function postFleetHalt(operatorId: string, reason: string) {
  try {
    const res = await fetch(`${API_BASE}/claw/halt`, {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ operatorId, reason }),
    });
    if (res.status === 401) { handle401(); return { error: 'Unauthorized' }; }
    if (!res.ok) throw new Error(`${res.status}`);
    return await res.json();
  } catch (e: any) {
    return { error: e.message };
  }
}

export async function postFleetResume() {
  try {
    const res = await fetch(`${API_BASE}/claw/resume`, {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
    });
    if (res.status === 401) { handle401(); return { error: 'Unauthorized' }; }
    if (!res.ok) throw new Error(`${res.status}`);
    return await res.json();
  } catch (e: any) {
    return { error: e.message };
  }
}

// Command Center - Discovery
export const fetchDiscoveryPreview = () => safeFetch(`${API_BASE}/cc/reports/preview?framework=soc2`);

// Command Center - Compliance
// Serves getAvailableFrameworks(), so the Compliance view no longer keeps its own
// hardcoded framework list. controlCount is what we have MAPPED, not the size of the
// framework: ISO 27001:2022 defines 93 Annex A controls and we map 15.
export const fetchComplianceFrameworks = () => safeFetch(`${API_BASE}/cc/compliance/frameworks`);

export async function postComplianceReport(framework: string) {
  try {
    const res = await fetch(`${API_BASE}/cc/reports/generate`, {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ framework }),
    });
    if (res.status === 401) { handle401(); return { error: 'Unauthorized' }; }
    if (!res.ok) throw new Error(`${res.status}`);
    return await res.json();
  } catch (e: any) {
    return { error: e.message };
  }
}

// Command Center - Security Status
export const fetchQuantumStatus = () => safeFetch(`${API_BASE}/quantum/status`);

// Online check -- uses auth key if present
export async function checkApiOnline(): Promise<boolean> {
  try {
    const res = await fetch('/health', { headers: authHeaders() });
    return res.ok;
  } catch {
    return false;
  }
}

// Auth mode probe: returns 'open' (no auth required), 'auth' (auth required), or 'offline'
export async function probeAuthMode(): Promise<'open' | 'auth' | 'offline'> {
  try {
    // First check if the server is reachable
    const h = await fetch('/health');
    if (!h.ok) return 'offline';

    // Probe an auth-gated endpoint (without credentials) to detect auth mode
    const res = await fetch(`${API_BASE}/metrics/enhanced`);
    if (res.status === 401) return 'auth';
    if (res.ok) return 'open';

    // 429 means rate-limited but auth passed (or not required) -- treat as open
    if (res.status === 429) return 'open';

    return 'auth';
  } catch {
    return 'offline';
  }
}
